import React, { useCallback, useEffect, useMemo } from "react"

import { useMetricsListRecordDataTransport } from "@/metrics/transport-data"
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { X } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { useEligibleParentMetrics } from "../../hooks/useEligibleParentMetrics"
import { convertResponseToMetricRecord } from "../../utils/createMetricInstance"
import { VALIDATION_MESSAGES } from "./validationMessages"

const FLOAT_OPERATORS = [">", ">=", "<", "<=", "==", "!="] as const
const STRING_OPERATORS = ["==", "!=", "in", "not_in"] as const

export interface StrictLogicCondition {
  parent_metric_id: string
  output_type: "float" | "string"
  operator: string
  threshold: number | string | string[]
}

export interface StrictLogicMetadata {
  derived_type: "strict_logic"
  conditions: StrictLogicCondition[]
  join_operator: "AND" | "OR"
  met_value: "MET"
  not_met_value: "NOT_MET"
}

interface Props {
  metadata: StrictLogicMetadata
  onChange: (next: StrictLogicMetadata) => void
  currentMetricId: string | null
}

function isMultiValueOperator(operator: string): boolean {
  return operator === "in" || operator === "not_in"
}

/**
 * Derive the partial condition update produced by selecting `selected` as the
 * parent for a condition whose current output type is `currentOutputType`.
 * Preserves operator/threshold when output_type is unchanged; resets to
 * defaults when it changes.
 */
export function computeParentToggleUpdate(
  selected: MetricRecordData,
  currentOutputType: "float" | "string",
): Partial<StrictLogicCondition> {
  const outputType: "float" | "string" =
    selected.output_type === "float" ? "float" : "string"
  if (outputType === currentOutputType) {
    return {
      parent_metric_id: selected.id,
      output_type: outputType,
    }
  }
  const defaultOperator = outputType === "float" ? ">=" : "=="
  return {
    parent_metric_id: selected.id,
    output_type: outputType,
    operator: defaultOperator,
    threshold: outputType === "float" ? 0 : "",
  }
}

const BINARY_OPTIONS = ["YES", "NO", "UNKNOWN"] as const

/**
 * Return a constrained option list when we know the parent metric's value
 * domain. Resolution order:
 *   1. The MetricRecord subclass's `getCategoricalOptions()` — covers built-in
 *      sentiment, end-reason, expected-match, etc.
 *   2. User-defined `metric_metadata.categories` (categorical LLM judges).
 *   3. Binary metrics fall back to YES/NO/UNKNOWN.
 *   4. Otherwise null — caller renders a free-text input.
 */
interface CategoricalCapableRecord {
  getCategoricalOptions: () => string[]
}

function hasCategoricalOptions(
  record: unknown,
): record is CategoricalCapableRecord {
  return (
    typeof record === "object" &&
    record !== null &&
    "getCategoricalOptions" in record &&
    typeof (record as CategoricalCapableRecord).getCategoricalOptions ===
      "function"
  )
}

function getStringOptions(
  parentMetric: MetricRecordData | null,
): string[] | null {
  if (!parentMetric) {
    return null
  }
  const record = convertResponseToMetricRecord(parentMetric)
  if (hasCategoricalOptions(record)) {
    const recordCategories = record.getCategoricalOptions()
    if (recordCategories.length > 0) {
      return recordCategories
    }
  }
  const categories = parentMetric.metric_metadata?.categories
  if (Array.isArray(categories) && categories.length > 0) {
    return categories
  }
  if (parentMetric.metric_metadata?.units === "binary") {
    return [...BINARY_OPTIONS]
  }
  return null
}

function ThresholdInput({
  condition,
  index,
  parentMetric,
  onUpdate,
}: {
  condition: StrictLogicCondition
  index: number
  parentMetric: MetricRecordData | null
  onUpdate: (index: number, updates: Partial<StrictLogicCondition>) => void
}) {
  const { output_type, operator, threshold } = condition

  const handleFloatChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      onUpdate(index, { threshold: raw === "" ? "" : Number(raw) })
    },
    [index, onUpdate],
  )

  const handleStringChange = useCallback(
    (value: string) => {
      onUpdate(index, { threshold: value })
    },
    [index, onUpdate],
  )

  const handleMultiValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      onUpdate(index, {
        threshold: raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      })
    },
    [index, onUpdate],
  )

  if (output_type === "float") {
    return (
      <Input
        type="number"
        value={typeof threshold === "number" ? String(threshold) : ""}
        onChange={handleFloatChange}
        placeholder="Threshold"
        aria-label={`Threshold for condition ${index + 1}`}
        className="w-32"
      />
    )
  }

  const options = getStringOptions(parentMetric)

  if (isMultiValueOperator(operator)) {
    const displayValue = Array.isArray(threshold) ? threshold.join(", ") : ""
    const placeholder =
      options?.slice(0, 2).join(", ") ?? "Comma-separated values"
    const ariaSuffix = options ? ` (options: ${options.join(", ")})` : ""
    return (
      <Input
        type="text"
        value={displayValue}
        onChange={handleMultiValueChange}
        placeholder={placeholder}
        aria-label={`Threshold values for condition ${index + 1} (comma-separated)${ariaSuffix}`}
        className="w-40"
      />
    )
  }

  const selectedValue = typeof threshold === "string" ? threshold : ""

  if (options === null) {
    return (
      <Input
        type="text"
        value={selectedValue}
        onChange={(e) => handleStringChange(e.target.value)}
        placeholder="Value"
        aria-label={`Threshold for condition ${index + 1}`}
        className="w-48"
      />
    )
  }

  return (
    <Select value={selectedValue} onValueChange={handleStringChange}>
      <SelectTrigger
        className="w-48"
        aria-label={`Threshold for condition ${index + 1}`}
      >
        <SelectValue placeholder="Value" />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface ConditionRowProps {
  condition: StrictLogicCondition
  index: number
  metrics: MetricRecordData[]
  currentMetricId: string | null
  onUpdate: (index: number, updates: Partial<StrictLogicCondition>) => void
  onRemove: (index: number) => void
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  index,
  metrics,
  currentMetricId,
  onUpdate,
  onRemove,
}) => {
  const selectedMetric = useMemo(
    () =>
      condition.parent_metric_id
        ? (metrics.find((m) => m.id === condition.parent_metric_id) ?? null)
        : null,
    [condition.parent_metric_id, metrics],
  )

  const { entries: parentOptions } = useEligibleParentMetrics({
    metrics,
    targetMetricType: "strict_logic",
    selfId: currentMetricId ?? undefined,
  })

  const handleToggle = useCallback(
    (metricId: string) => {
      const selected = metrics.find((m) => m.id === metricId)
      if (!selected) {
        return
      }
      onUpdate(
        index,
        computeParentToggleUpdate(selected, condition.output_type),
      )
    },
    [index, metrics, onUpdate, condition.output_type],
  )

  const handleOperatorChange = useCallback(
    (value: string) => {
      const wasMulti = isMultiValueOperator(condition.operator)
      const willBeMulti = isMultiValueOperator(value)
      let threshold = condition.threshold
      if (wasMulti && !willBeMulti) {
        threshold = ""
      } else if (!wasMulti && willBeMulti) {
        threshold = []
      }
      onUpdate(index, { operator: value, threshold })
    },
    [index, condition.operator, condition.threshold, onUpdate],
  )

  const handleRemove = useCallback(() => {
    onRemove(index)
  }, [index, onRemove])

  const operators =
    condition.output_type === "float" ? FLOAT_OPERATORS : STRING_OPERATORS

  return (
    <div className="flex flex-col gap-3 p-3 border border-divider rounded-lg bg-content1">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Select value={selectedMetric?.id ?? ""} onValueChange={handleToggle}>
            <SelectTrigger
              className="w-full"
              aria-label={`Parent metric for condition ${index + 1}`}
            >
              {selectedMetric ? (
                <span className="flex flex-1 items-center gap-2 truncate text-left">
                  <span className="flex-1 truncate text-sm font-medium">
                    {selectedMetric.metric_display_name ||
                      selectedMetric.metric_name}
                  </span>
                  <span className="flex-shrink-0 text-xs text-coval-text-muted">
                    {[
                      condition.output_type,
                      selectedMetric.metric_metadata?.units,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              ) : (
                <SelectValue placeholder="Select a parent metric…" />
              )}
            </SelectTrigger>
            <SelectContent>
              {parentOptions.map(({ metric, eligibility }) => (
                <SelectItem
                  key={metric.id}
                  value={metric.id ?? ""}
                  disabled={!eligibility.eligible}
                  title={!eligibility.eligible ? eligibility.reason : undefined}
                >
                  <span className="flex w-full items-center justify-between gap-3">
                    <span className="flex-1 truncate">
                      {metric.metric_display_name || metric.metric_name}
                    </span>
                    {(metric.output_type || metric.metric_metadata?.units) && (
                      <span className="text-xs text-coval-text-muted">
                        {[metric.output_type, metric.metric_metadata?.units]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          aria-label={`Remove condition ${index + 1}`}
          className="mt-2 text-coval-text-muted hover:text-coval-danger-text flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {selectedMetric && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={condition.operator}
            onValueChange={handleOperatorChange}
          >
            <SelectTrigger
              className="w-28"
              aria-label={`Operator for condition ${index + 1}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operators.map((op) => (
                <SelectItem key={op} value={op}>
                  {op}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ThresholdInput
            condition={condition}
            index={index}
            parentMetric={selectedMetric}
            onUpdate={onUpdate}
          />
        </div>
      )}
    </div>
  )
}

export function validateStrictLogicMetadata(
  metadata: StrictLogicMetadata,
): string | null {
  const conditions = metadata?.conditions ?? []
  if (conditions.length === 0) {
    return VALIDATION_MESSAGES.STRICT_LOGIC_NO_CONDITIONS
  }
  for (const cond of conditions) {
    if (!cond.parent_metric_id || !cond.operator) {
      return VALIDATION_MESSAGES.STRICT_LOGIC_INCOMPLETE_CONDITION
    }
    if (cond.output_type === "float") {
      if (typeof cond.threshold !== "number") {
        return VALIDATION_MESSAGES.STRICT_LOGIC_INCOMPLETE_CONDITION
      }
    } else if (isMultiValueOperator(cond.operator)) {
      if (!Array.isArray(cond.threshold) || cond.threshold.length === 0) {
        return VALIDATION_MESSAGES.STRICT_LOGIC_INCOMPLETE_CONDITION
      }
    } else if (typeof cond.threshold !== "string" || cond.threshold === "") {
      return VALIDATION_MESSAGES.STRICT_LOGIC_INCOMPLETE_CONDITION
    }
  }
  return null
}

export const DerivedStrictLogicConfig: React.FC<Props> = ({
  metadata,
  onChange,
  currentMetricId,
}) => {
  const { data: metricsData } = useMetricsListRecordDataTransport()
  const metrics: MetricRecordData[] = useMemo(
    () => Object.values(metricsData ?? {}),
    [metricsData],
  )

  // Tolerate metadata persisted before the conditions field existed.
  const conditions = useMemo(
    () => metadata.conditions ?? [],
    [metadata.conditions],
  )

  const joinOperator: "AND" | "OR" = metadata.join_operator ?? "AND"

  // Backfill required fields when metadata was saved before they existed so
  // the next save round-trips a complete StrictLogicMetadata.
  useEffect(() => {
    const patch: Partial<StrictLogicMetadata> = {}
    if (!metadata.join_operator) {
      patch.join_operator = "AND"
    }
    if (!metadata.met_value) {
      patch.met_value = "MET"
    }
    if (!metadata.not_met_value) {
      patch.not_met_value = "NOT_MET"
    }
    if (Object.keys(patch).length > 0) {
      onChange({ ...metadata, ...patch })
    }
  }, [metadata, onChange])

  const handleJoinOperatorChange = useCallback(
    (value: string) => {
      if (value === "AND" || value === "OR") {
        onChange({ ...metadata, join_operator: value })
      }
    },
    [metadata, onChange],
  )

  const handleAddCondition = useCallback(() => {
    const newCondition: StrictLogicCondition = {
      parent_metric_id: "",
      output_type: "string",
      operator: "==",
      threshold: "",
    }
    onChange({
      ...metadata,
      conditions: [...conditions, newCondition],
    })
  }, [metadata, conditions, onChange])

  const handleUpdateCondition = useCallback(
    (index: number, updates: Partial<StrictLogicCondition>) => {
      const updated = conditions.map((c, i) =>
        i === index ? { ...c, ...updates } : c,
      )
      onChange({ ...metadata, conditions: updated })
    },
    [metadata, conditions, onChange],
  )

  const handleRemoveCondition = useCallback(
    (index: number) => {
      onChange({
        ...metadata,
        conditions: conditions.filter((_, i) => i !== index),
      })
    },
    [metadata, conditions, onChange],
  )

  const validationError = useMemo(
    () => validateStrictLogicMetadata(metadata),
    [metadata],
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="space-y-2">
            <Label>Join operator</Label>
            <RadioGroup
              value={joinOperator}
              onValueChange={handleJoinOperatorChange}
              aria-label="Join operator"
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="AND" id="join-and" aria-label="AND" />
                <label htmlFor="join-and" className="text-sm cursor-pointer">
                  AND — All conditions must pass for MET
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="OR" id="join-or" aria-label="OR" />
                <label htmlFor="join-or" className="text-sm cursor-pointer">
                  OR — Any condition must pass for MET
                </label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <Label>Conditions</Label>

          {conditions.length === 0 && (
            <p className="text-sm text-coval-text-muted">
              No conditions yet. Add at least one condition.
            </p>
          )}

          {conditions.map((condition, index) => (
            <ConditionRow
              key={`${index}-${condition.parent_metric_id ?? "empty"}`}
              condition={condition}
              index={index}
              metrics={metrics}
              currentMetricId={currentMetricId}
              onUpdate={handleUpdateCondition}
              onRemove={handleRemoveCondition}
            />
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={handleAddCondition}
            aria-label="Add condition"
          >
            Add condition
          </Button>

          {validationError && (
            <p className="text-sm text-coval-danger-text" role="alert">
              {validationError}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
